import { Component, Input, OnDestroy, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { TargetingResultModel } from '../../../../Discover/Targeting/models/targeting-result.model';
import { TargetingSearchService } from '../../../../Discover/Targeting/services/targeting-search.service';
import { ContactService } from '../../../TargetingShared/services/contact-service';
import { DiscoverRoutingService } from '../../../../Discover/DiscoverRouter/services/discover-routing.service';
import { TargetingListService } from '../../../../Discover/Targeting/services/targeting-list.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ListPickerService } from '../../services/list-picker.service';
import { ContactsPublicApi } from '../../../../api/targeting/contacts-public.api';
import { TargetingRhpContextsService } from '../../../TargetingShared/services/targeting-rhp-contexts.service';
import { EmploymentFactory } from '../../../../Discover/Targeting/factories/employment.factory';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TargetingRhpDialogService } from '../../../TargetingShared/services/targeting-rhp-dialog.service';
import { TargetingSearch } from '@Discover/Targeting/models/targeting-search.model';
import { ContactCardComponent } from '../ContactCard/contact-card.component';
import { IncludeExcludeModel } from '@Common/Misc/models/include-exclude-model';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { JournodexSubjectsService, SubjectUtils } from '@Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '@Discover/Core/services/journodex-enums.service';
import { DistributionPickerService } from '../../services/distribution-picker.service';
import { EmailConversationService } from '../../../../Discover/Targeting/services/email-conversation.service';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';


@Component({
    selector: 'agpr-recommended-contact-card',
    templateUrl: './recommended-contact-card.component.html',
    styleUrls: ['../../../TargetingShared/components/ResultCard/result-card.scss', './recommended-contact-card.component.scss'],
    animations: [
        trigger('showMoreContent', [
            state('collapsed', style({
                height: '0',
            })),
            state('expanded', style({
                height: '*',
            })),
            transition('expanded <=> collapsed', animate('400ms cubic-bezier(.43,.43,.65,.99)'))
        ])
    ],
    standalone: false
})
export class RecommendedContactCardComponent extends ContactCardComponent implements OnDestroy {

    @Input() set entity(value: TargetingResultModel) {
        this.entityInternal = value;
        this.updateListMembershipInfo();
        this.setDisplayedReachValue();
        this.setDetailsSection();
    }

    @Input() set searchModel(value: TargetingSearch) {
        this._searchModel = value;
        this.setDetailsSection();
    }

    @Input() showBlueBadge: boolean = false;
    @Input() blueBadgeTooltip: string;

    targetAudienceRegions: IncludeExcludeModel<number>;
    mediaMarkets: IncludeExcludeModel<number>;
    subjects: IncludeExcludeModel<number>;
    coverageFocuses: IncludeExcludeModel<String>;
    marketSectors: IncludeExcludeModel<String>;
    targetAudiences: IncludeExcludeModel<String>;

    _searchModel: TargetingSearch;

    constructor(
        public targetingSearchService: TargetingSearchService,
        public targetingListService: TargetingListService,
        public listPickerService: ListPickerService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public contactService: ContactService,
        public discoverRoutingService: DiscoverRoutingService,
        public contactsPublicApi: ContactsPublicApi,
        public targetingRhpContextsService: TargetingRhpContextsService,
        protected employmentFactory: EmploymentFactory,
        public targetingRhpDialogService: TargetingRhpDialogService,
        public journodexRegionsService: JournodexRegionsService,
        public journodexSubjectsService: JournodexSubjectsService,
        public journodexEnumsService: JournodexEnumsService,
        public distributionPickerService: DistributionPickerService,
        @Inject (APP_BASE_HREF) public appBaseHref: string,
        public dataConnectionService: DataConnectionService,
        public emailConversationService: EmailConversationService
    ) {
        super(
            targetingSearchService,
            targetingListService,
            listPickerService,
            localizationService,
            coreDialogService,
            contactService,
            discoverRoutingService,
            contactsPublicApi,
            targetingRhpContextsService,
            employmentFactory,
            targetingRhpDialogService,
            distributionPickerService,
            appBaseHref,
            dataConnectionService,
            emailConversationService
        );
        this.subscriptions.push(
            this.targetingRhpContextsService.getContactId().subscribe(contactId => {
                this.selectedContactId = contactId;
            }),
            this.targetingSearchService.rhpSelectedEmployment().subscribe(res => {
                if (res) {
                    this.rhpSelectedEmploymentChanged();
                }
            }),
        );
    }

    setDetailsSection() {
        if (this._searchModel && this.entityInternal) {
            let count = 0;

            // areasOfCoverage/targetAudienceRegions
            if (this._searchModel?.areasOfCoverage?.include.length) {
                this._searchModel?.areasOfCoverage?.include.forEach(areaOfCoverageId => {
                    this.entityInternal?.selectedEmployment?.outlet?.areasOfCoverage.forEach(outletAreaOfCoverageId => {
                        if (areaOfCoverageId === outletAreaOfCoverageId) {
                            if (!this.targetAudienceRegions) {
                                this.targetAudienceRegions = new IncludeExcludeModel();
                            }
                            this.targetAudienceRegions.include = [...[areaOfCoverageId]];
                            return;
                        }
                    });
                });

                if (this.targetAudienceRegions?.include?.length) {
                    count++;
                } else {
                    this.targetAudienceRegions = null;
                }
            } else {
                this.targetAudienceRegions = null;
            }

            // mediaMarkets
            if (this.entityInternal?.selectedEmployment?.outlet?.mediaMarkets?.length && this._searchModel?.mediaMarkets?.include.length) {
                this._searchModel?.mediaMarkets?.include.forEach(mediaMarketId => {
                    this.entityInternal?.selectedEmployment?.outlet?.mediaMarkets.forEach(outletMediaMarket => {
                        if (mediaMarketId === outletMediaMarket.id) {
                            if (!this.mediaMarkets) {
                                this.mediaMarkets = new IncludeExcludeModel();
                            }
                            this.mediaMarkets.include = [...[mediaMarketId]];
                            return;
                        }
                    });
                });
                if (this.mediaMarkets?.include?.length) {
                    count++;
                } else {
                    this.mediaMarkets = null;
                }
            } else {
                this.mediaMarkets = null;
            }

            if (count < 2) {
                // subjects
                if (this.entityInternal?.contactModel?.subjects?.length && this._searchModel?.subjects?.include.length) {
                    this._searchModel?.subjects?.include.forEach(subjectId => {
                        if (SubjectUtils.doesIdContainInSubjects(this.entityInternal?.contactModel?.subjects, subjectId)) {
                            if (!this.subjects) {
                                this.subjects = new IncludeExcludeModel();
                            }
                            this.subjects.include = [...[subjectId]];
                        }
                    });
                    if (this.subjects?.include?.length) {
                        count++;
                    } else {
                        this.subjects = null;
                    }
                } else {
                    this.subjects = null;
                }
            }

            if (count < 2) {
                // coverageFocus
                if (this._searchModel?.coverageFocuses?.include.includes(this.entityInternal?.selectedEmployment?.outlet?.coverageFocus?.enumValue)) {
                    this.coverageFocuses = new IncludeExcludeModel(this._searchModel?.coverageFocuses);
                    count++;
                } else {
                    this.coverageFocuses = null;
                }
            }

            if (count < 2) {
                // marketSectors
                if (this._searchModel?.marketSectors?.include.includes(this.entityInternal?.selectedEmployment?.outlet?.marketSector?.enumValue)) {
                    this.marketSectors = new IncludeExcludeModel(this._searchModel?.marketSectors);
                    count++;
                } else {
                    this.marketSectors = null;
                }
            }

            if (count < 2) {
                // targetAudiences
                if (this.entityInternal?.selectedEmployment?.outlet?.targetAudiences?.length && this._searchModel?.targetAudiences?.include.length) {
                    this._searchModel?.targetAudiences?.include.forEach(targetAudience => {
                        this.entityInternal?.selectedEmployment?.outlet?.targetAudiences.forEach(outletTargetAudience => {
                            if (targetAudience === outletTargetAudience.enumValue) {
                                if (!this.targetAudiences) {
                                    this.targetAudiences = new IncludeExcludeModel();
                                }
                                this.targetAudiences.include = [...[targetAudience]];
                                return;
                            }
                        });
                    });
                    if (this.targetAudiences?.include?.length) {
                        count++;
                    } else {
                        this.targetAudiences = null;
                    }
                } else {
                    this.targetAudiences = null;
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
